import { useState, useEffect } from 'react';
import { useGetSwarmsQuery } from '../../../../../../../services/swarmsApi';
import { useNotification } from '../../../../../../../context/NotificationContext';
import { createErrorMessage } from '../../../../../../../utils';

/**
 * Custom hook for fetching and handling swarm data
 * @param {number} bteId - The BTE ID to fetch swarm data
 * @param {boolean} isOpen - Whether the modal is open
 * @returns {Object} The swarm data, loading state, and error state
 */
export default function useSwarmData(bteId, isOpen) {
  const { 
    data: swarmApiRes, 
    isLoading, 
    isFetching, 
    isError, 
    error 
  } = useGetSwarmsQuery("?bteId=" + bteId, { skip: !bteId || !isOpen });

  const { showNotification } = useNotification();

  // State to track data validation
  const [isValid, setIsValid] = useState(false);
  
  // Validate we have exactly one swarm and one event
  useEffect(() => {
    if (!isOpen || !swarmApiRes) {
      setIsValid(false);
      return;
    }

    if (!swarmApiRes.swarms?.length || !swarmApiRes.events?.length) {
      console.error('No data found for bteId:', bteId);
      showNotification('No data found for this event', 'error');
      setIsValid(false);
      return;
    }

    if (swarmApiRes.swarms.length > 1 || swarmApiRes.events.length > 1) {
      console.error('Multiple results found for bteId:', bteId, 'Expected exactly one result');
      showNotification('Unexpected data: Multiple results found for this event', 'error');
      setIsValid(false);
      return;
    }

    setIsValid(true);
  }, [isOpen, swarmApiRes, bteId, showNotification]);

  return {
    swarmApiRes,
    isLoading,
    isFetching,
    isError,
    error,
    isValid,
    swarm: swarmApiRes?.swarms?.[0] || null,
    event: swarmApiRes?.events?.[0] || null
  };
}
